import { Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  WorkflowStepInstanceCommentTypes,
  type WorkflowStepInstanceComment,
} from "~/api/types";
import { useUserContext } from "~/hooks/useUserContext";
import { getCommentLabel } from "~/helpers/workflow-instances";
import { cn } from "~/lib/utils";
import AvatarWithFallback from "./AvatarWithFallback";

export const StepComments = ({
  comments,
}: {
  comments: WorkflowStepInstanceComment[];
}) => {
  const { t } = useTranslation();
  const { user } = useUserContext();

  if (comments.length === 0) return null;

  return (
    <div className="space-y-3 border-t pt-3">
      <div className="space-y-3">
        {comments.map((comment) => {
          const isCurrentUser = comment.createdBy.userId === user.id;

          const avatar = (
            <AvatarWithFallback
              user={comment.createdBy}
              size="sm"
              showTooltip
            />
          );

          return (
            <div
              key={comment.id}
              className="grid grid-cols-[auto_1fr_auto] items-start gap-1"
            >
              <div>{!isCurrentUser && avatar}</div>

              <div
                className={cn(
                  "max-w-full min-w-0 rounded-lg border bg-white p-3",
                  isCurrentUser
                    ? "justify-self-end rounded-tr-none"
                    : "justify-self-start rounded-tl-none",
                  {
                    "border-red-200 bg-red-50":
                      comment.type ===
                      WorkflowStepInstanceCommentTypes.rejected,
                    "border-green-200 bg-green-50":
                      comment.type ===
                      WorkflowStepInstanceCommentTypes.approved,
                  },
                )}
              >
                <div className="text-muted-foreground flex items-center gap-1 text-xs">
                  {comment.type ===
                    WorkflowStepInstanceCommentTypes.rejected && (
                    <X className="h-3 w-3 text-red-700" />
                  )}
                  {comment.type ===
                    WorkflowStepInstanceCommentTypes.approved && (
                    <Check className="h-3 w-3 text-emerald-700" />
                  )}
                  <span>{getCommentLabel(comment, t)}</span>
                </div>
                <p className="text-sm wrap-break-word whitespace-pre-wrap">
                  {comment.text}
                </p>
              </div>

              <div>{isCurrentUser && avatar}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
